/**
 * TopFlight Protection — customer site content (owner ask 2026-11-03).
 * Template: security-pro (light + dark). All wording is original.
 * Logos are the owner's own files (public/topflight/).
 */
export const topflightConfig = {
  slug: 'topflight',
  business_name: 'TopFlight Protection',
  industry: 'security',
  template_id: 'security-pro',
  custom_domain: 'www.securitytopflight.com',
  // The Agent9 badge says "AI real estate assistant": wrong for a security firm.
  hide_attribution: true,

  phone_number: '+12092096668',
  phone_display: '(209) 209-6668',
  email_public: 'vinny@securitytopflight.com',
  address: {
    street: '1422 Grove Ave',
    city: 'Manteca',
    region: 'CA',
    postal: '95337',
    country: 'US',
  },
  service_area: 'All of California',
  hours: 'Office: Mon–Fri 8:00 AM – 5:00 PM · Officers on duty 24/7',
  tagline: 'Commercial Security Services',

  leadership: [
    {
      name: 'Vinny Carranza',
      title: 'Head of Security',
      phone: '+12096249578',
      phone_display: '(209) 624-9578',
      email: 'vinny@securitytopflight.com',
    },
  ],

  logo: {
    light: '/topflight/logo-light.png', // navy wordmark, for the light theme
    dark: '/topflight/logo-dark.png', // white wordmark, for the dark theme
    mark: '/topflight/mark-512.png',
    full_dark: '/topflight/logo-dark-full.jpg',
    favicon_ico: '/topflight/favicon.ico',
    favicon_32: '/topflight/favicon-32.png',
    favicon_192: '/topflight/favicon-192.png',
  },

  // TopFlight's own branded photos (sent by the owner 2026-11-03).
  photos: {
    hero: {
      src: '/topflight/night-patrol-1533.jpg',
      srcSet: '/topflight/night-patrol-900.jpg 900w, /topflight/night-patrol-1533.jpg 1533w',
      alt: 'TopFlight Security patrol unit and officer outside a client building at night',
    },
    about: {
      src: '/topflight/fleet-1434.jpg',
      srcSet: '/topflight/fleet-900.jpg 900w, /topflight/fleet-1434.jpg 1434w',
      alt: 'TopFlight Security officers with their marked patrol cars',
      position: '50% 70%',
    },
    industries: {
      src: '/topflight/port-patrol-1642.jpg',
      srcSet: '/topflight/port-patrol-900.jpg 900w, /topflight/port-patrol-1642.jpg 1642w',
      alt: 'TopFlight Security officer logging a patrol at a port container yard',
      position: '50% 55%',
    },
    team: {
      src: '/topflight/team-1776.jpg',
      srcSet: '/topflight/team-900.jpg 900w, /topflight/team-1776.jpg 1776w',
      alt: 'TopFlight Security officers in TopFlight vests',
      position: '50% 30%',
    },
    // Event briefing, cropped to the TopFlight team (the venue's signage is left out).
    careers: {
      src: '/topflight/event-team-845.jpg',
      alt: 'TopFlight Security event team at a pre-shift briefing',
      position: '50% 35%',
    },
    why: {
      src: '/topflight/patrol-officer-1084.jpg',
      srcSet: '/topflight/patrol-officer-720.jpg 720w, /topflight/patrol-officer-1084.jpg 1084w',
      alt: 'TopFlight Security patrol officer on the radio beside a TopFlight patrol car',
      position: '50% 22%',
    },
  },

  seo: {
    title: 'TopFlight Protection | Security Guard Services in California',
    description:
      'Licensed armed and unarmed security officers, mobile patrol, event, construction, retail and private security across California. Free security assessment: (209) 209-6668.',
    canonical: 'https://www.securitytopflight.com/',
    keywords: [
      'security guard company California', 'armed security guards', 'unarmed security officers',
      'mobile patrol', 'construction site security', 'event security', 'fire watch',
      'executive protection', 'Manteca security company', 'Central Valley security guards',
    ],
  },

  hero: {
    eyebrow: 'Licensed · Insured · On duty 24/7',
    headline: 'Security that stands watch, so you can focus on business.',
    subheadline:
      'Trained armed and unarmed officers, patrols and monitoring for businesses, communities and private clients anywhere in California.',
    primary_cta: 'Get a free assessment',
    secondary_cta: 'Call (209) 209-6668',
  },

  stats: [
    { value: '24/7', label: 'Officers and dispatch' },
    { value: 'Statewide', label: 'Coverage across California' },
    { value: 'Armed + unarmed', label: 'Licensed officers' },
    { value: 'Free', label: 'Site security assessment' },
  ],

  service_groups: [
    {
      id: 'commercial',
      title: 'Commercial security',
      intro: 'Uniformed protection for the places you do business.',
      services: [
        { id: 'unarmed', icon: 'ShieldCheck', name: 'Unarmed security officers', text: 'Visible, professional officers for access control, patrols, customer assistance and incident reports.' },
        { id: 'armed', icon: 'BadgeCheck', name: 'Armed security officers', text: 'Firearm-permitted officers for higher-risk sites, cash handling and situations that call for a stronger presence.' },
        { id: 'patrol', icon: 'Car', name: 'Mobile patrol', text: 'Marked vehicle patrols with randomized checks, lock-ups and unlocks, and a report after every visit.' },
        { id: 'construction', icon: 'HardHat', name: 'Construction site security', text: 'Night and weekend coverage that protects equipment, materials and copper from theft and vandalism.' },
        { id: 'retail', icon: 'ShoppingBag', name: 'Retail and loss prevention', text: 'Officers who deter shoplifting, support staff and keep stores and shopping centers calm.' },
        { id: 'front-desk', icon: 'Building2', name: 'Front desk and concierge', text: 'Lobby officers who greet visitors, check credentials and keep your building running smoothly.' },
        { id: 'access', icon: 'KeyRound', name: 'Access control and gates', text: 'Gate and checkpoint officers who verify every person and vehicle that enters your site.' },
        { id: 'parking', icon: 'TrafficCone', name: 'Parking and traffic control', text: 'Lot patrols, traffic direction and enforcement for venues, campuses and busy properties.' },
      ],
    },
    {
      id: 'private',
      title: 'Private security',
      intro: 'Discreet protection for homes, families and private events.',
      services: [
        { id: 'residential', icon: 'Home', name: 'Residential and HOA patrol', text: 'Community patrols, gatehouse officers and rule enforcement for HOAs, apartments and neighborhoods.' },
        { id: 'estate', icon: 'Landmark', name: 'Estate security', text: 'Standing officers and patrols for private homes and estates, day or night.' },
        { id: 'executive', icon: 'UserCheck', name: 'Executive and personal protection', text: 'Close protection for executives, public figures and families at home, at work and while traveling.' },
        { id: 'private-events', icon: 'PartyPopper', name: 'Private event security', text: 'Weddings, parties and private gatherings with guest-list checks and calm, courteous officers.' },
      ],
    },
    {
      id: 'specialty',
      title: 'Specialty and technology',
      intro: 'Extra layers of protection when you need them.',
      services: [
        { id: 'events', icon: 'Ticket', name: 'Event security', text: 'Crowd management, bag checks and entry control for concerts, festivals, sports and corporate events.' },
        { id: 'fire-watch', icon: 'Flame', name: 'Fire watch', text: 'Trained fire watch officers when alarms or sprinklers are down, with logs your fire marshal accepts.' },
        { id: 'video', icon: 'Cctv', name: 'Remote video monitoring', text: 'Live eyes on your cameras, with officers or police dispatched when something is wrong.' },
        { id: 'alarm', icon: 'BellRing', name: 'Alarm response', text: 'An officer sent to check out alarms, so you are not the one driving over at 2 AM.' },
        { id: 'consulting', icon: 'ClipboardCheck', name: 'Risk assessments and consulting', text: 'A walk-through of your site and a clear plan: posts, patrols, lighting, cameras and procedures.' },
        { id: 'systems', icon: 'ScanFace', name: 'Camera and access systems', text: 'Help choosing and setting up cameras and access control that work with your officers.' },
      ],
    },
  ],

  industries: [
    { icon: 'Building', name: 'Commercial offices' },
    { icon: 'HardHat', name: 'Construction' },
    { icon: 'Store', name: 'Retail and shopping centers' },
    { icon: 'Warehouse', name: 'Warehouses and logistics' },
    { icon: 'Home', name: 'HOAs and residential' },
    { icon: 'HeartPulse', name: 'Healthcare' },
    { icon: 'GraduationCap', name: 'Schools and campuses' },
    { icon: 'Ticket', name: 'Events and venues' },
    { icon: 'Factory', name: 'Manufacturing and industrial' },
    { icon: 'Hotel', name: 'Hotels and hospitality' },
    { icon: 'Landmark', name: 'Government and public sites' },
    { icon: 'Leaf', name: 'Cannabis and agriculture' },
  ],

  why: [
    { icon: 'BadgeCheck', title: 'Licensed and insured', text: 'Every officer holds a California guard card, and armed officers hold firearm permits. Proof of insurance comes with every quote.' },
    { icon: 'Users', title: 'Officers you can trust', text: 'Background-checked, trained for your site and supervised in the field.' },
    { icon: 'Radio', title: 'Always reachable', text: 'A live answer day and night, and a supervisor who picks up when it matters.' },
    { icon: 'FileText', title: 'Clear reporting', text: 'Daily activity reports and incident reports, so you always know what happened on your site.' },
  ],

  steps: [
    { title: 'Assess', text: 'We walk your site and listen to what worries you.' },
    { title: 'Plan', text: 'You get a written plan and a clear quote, with no surprises.' },
    { title: 'Deploy', text: 'Trained officers start on your schedule, often within days.' },
    { title: 'Report', text: 'Regular reports and a direct line to your supervisor.' },
  ],

  faqs: [
    { q: 'How much does a security guard cost?', a: 'Every site is different, so we quote after a free security assessment. The price depends on armed or unarmed officers, the hours and the duties.' },
    { q: 'How quickly can officers start?', a: 'Often within a few days, and sooner for urgent needs when officers are available. We confirm the start date with your quote.' },
    { q: 'Do you serve my city?', a: 'We serve all of California, from our office in Manteca.' },
    { q: 'Armed or unarmed: which do I need?', a: 'Most sites are well served by unarmed officers. Armed officers fit higher-risk sites. We recommend the right fit during your assessment.' },
    { q: 'Do you protect homes and private clients?', a: 'Yes: residential and HOA patrol, estate security, private events and executive protection.' },
    { q: 'Can I call any time?', a: 'Yes. Our line is answered 24/7, and we can book your assessment on the call.' },
  ],

  careers: {
    headline: 'Join the TopFlight team',
    text: 'We are hiring licensed security officers across California. Steady hours, fair pay, and supervisors who have your back.',
    requirements: [
      'Valid California guard card (BSIS)',
      'Firearm permit for armed posts',
      'Reliable transportation and a phone',
      'Clean background and professional attitude',
    ],
  },

  quote_form: {
    services: [
      'Unarmed security officers', 'Armed security officers', 'Mobile patrol',
      'Construction site security', 'Retail and loss prevention', 'Event security',
      'Fire watch', 'Remote video monitoring', 'Residential / HOA patrol',
      'Executive or personal protection', 'Private event security', 'Risk assessment / consulting',
      'Something else',
    ],
    success_message: 'Thank you. Your request is in, and a TopFlight manager will contact you shortly to set up your free security assessment.',
  },
};

export default topflightConfig;
